import { BASE_KIOSK_API } from '@/core/api-config';
import { TokenManager } from "@/core/auth/token-manager";
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import {
  SessionsListRequest,
  SessionDetailRequest,
  SessionsListResponse,
  SessionDetailResponse,
  CountOnlyResponse,
  SlimLocation
} from '../types/api-types';

/**
 * Kiosk Session API client
 * Handles authenticated requests to the kiosk sessions endpoints
 * Extends the existing authentication pattern from AuthApi
 */
export class KioskSessionApi {
  constructor(
    private readonly baseUrl: string = BASE_KIOSK_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Fetches list of kiosk sessions with filtering
   * GET /admin/sessions?operatorId=<ID>&from=<YYYY-MM-DD>&to=<YYYY-MM-DD>[&locationId=<ID>][&limit=<number>][&skip=<number>][&count=<boolean>]
   *
   * @returns SessionsListResponse when count=false (full data)
   * @returns CountOnlyResponse when count=true (just {count: number})
   */
  async getSessionsList(params: SessionsListRequest): Promise<SessionsListResponse | CountOnlyResponse> {
    const queryParams = new URLSearchParams({
      operatorId: params.operatorId,
      from: params.from,
      to: params.to
    });

    if (params.locationId) {
      queryParams.set('locationId', params.locationId);
    }

    if (params.subOperatorId) {
      queryParams.set('subOperatorId', params.subOperatorId);
    }

    if (params.includeSubOperators) {
      queryParams.set('includeSubOperators', 'true');
    }

    if (params.limit) {
      queryParams.set('limit', params.limit.toString());
    }

    // Add skip parameter for pagination support (skip = currentPage * pageSize)
    if (params.skip !== undefined) {
      queryParams.set('skip', params.skip.toString());
    }

    // Add count parameter for pagination support
    if (params.count !== undefined) {
      queryParams.set('count', params.count.toString());
    }

    // Add transaction ID filter (server-side)
    if (params.transactionId !== undefined) {
      queryParams.set('transactionId', params.transactionId.toString());
    }

    // Add session ID filter (server-side)
    if (params.sessionId !== undefined) {
      queryParams.set('sessionId', params.sessionId);
    }

    // Add settlement filter (server-side): true = no-settlement, false = settled only, omit = all
    if (params.noSettlement !== undefined) {
      queryParams.set('noSettlement', params.noSettlement.toString());
    }

    // Add status filter (server-side, comma-separated)
    if (params.statuses?.length) {
      queryParams.set('status', params.statuses.join(','));
    }

    const endpoint = `/admin/sessions?${queryParams.toString()}`;
    return this.authenticatedRequest<SessionsListResponse>(endpoint);
  }

  /**
   * Fetches detailed information for a specific session
   * GET /admin/session?operatorId=<ID>&sessionId=<ID>
   */
  async getSessionDetail(params: SessionDetailRequest): Promise<SessionDetailResponse | any> {
    const queryParams = new URLSearchParams({
      operatorId: params.operatorId,
      sessionId: params.sessionId
    });

    const endpoint = `/admin/session?${queryParams.toString()}`;
    return this.authenticatedRequest<SessionDetailResponse>(endpoint);
  }

  /**
   * Fetches slim OCPI locations for an operator (without EVSEs/connectors)
   * GET /admin/locations?operatorId=<ID>&slim=true
   */
  async getSlimLocations(operatorId: string): Promise<SlimLocation[]> {
    const queryParams = new URLSearchParams({
      operatorId,
      slim: 'true'
    });

    const endpoint = `/admin/locations?${queryParams.toString()}`;
    return this.authenticatedRequest<SlimLocation[]>(endpoint);
  }

  /**
   * Makes an authenticated request to the API
   * Follows the same pattern as AuthApi.authenticatedRequest
   */
  private async authenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = await this.tokenManager.getAuthHeaders();
    
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${endpoint}`,
      {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
          ...options.headers,
        },
      },
      'Kiosk API',
      endpoint
    );

    return response.json();
  }

  /**
   * Validates that required tokens are available
   * Throws error if authentication is not available
   */
  private async validateAuthentication(): Promise<void> {
    const isValid = await this.tokenManager.isTokenValid();
    if (!isValid) {
      throw new Error('Authentication required. Please log in to access kiosk sessions.');
    }
  }

  /**
   * Health check method to test API connectivity
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.validateAuthentication();
      // Could implement a simple ping endpoint if available
      return true;
    } catch (error) {
      console.error('Kiosk Session API connection test failed:', error);
      return false;
    }
  }
}